import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/api";
import { PenSquare, Upload, Image, Loader2, Copy, Check, Facebook, Instagram, Share2, Edit2, Download, Store, Phone, MapPin, RefreshCw, X, Plus, Clock, ChevronRight, Trash2, Sparkles, ImagePlus, Bookmark, BookmarkCheck } from "lucide-react";
import "./CreatePost.css";

type Platform = "facebook" | "instagram" | "tiktok";
interface FlyerDesign {
  bgColor: string;
  bgColorEnd: string;
  accentColor: string;
  headlineColor: string;
  textColor: string;
  badgeBg: string;
  badgeText: string;
  features: string[];
  decorStyle: "minimal" | "bold" | "elegant" | "festive";
}

interface PostResult {
  postText: string;
  productName: string;
  estimatedPrice?: string;
  productCategory?: string;
  flyerHeadline?: string;
  flyerSubheadline?: string;
  promoLine?: string;
  callToAction?: string;
  tags?: string[];
  flyerDesign?: FlyerDesign;
}

interface HistoryEntry {
  id: string;
  timestamp: number;
  platform: Platform;
  platform_label: string;
  result: PostResult;
  hashtags: string[];
  flyerDataUrl?: string;
  imagePreview?: string;
}

const PLATFORM_LABELS: Record<Platform, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
};


function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = src;
  });
}

function drawStarburst(ctx: CanvasRenderingContext2D, cx: number, cy: number, outerR: number, innerR: number, points: number) {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const angle = (Math.PI * i) / points - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, startSize: number, minSize: number, font: string): number {
  for (let size = startSize; size >= minSize; size -= 2) {
    ctx.font = `900 ${size}px ${font}`;
    if (ctx.measureText(text).width <= maxWidth) return size;
  }
  return minSize;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function lighten(hex: string, pct: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = pct / 100;
  return `rgb(${Math.min(255, Math.round(r + (255 - r) * f))},${Math.min(255, Math.round(g + (255 - g) * f))},${Math.min(255, Math.round(b + (255 - b) * f))})`;
}

function darken(hex: string, pct: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - pct / 100;
  return `rgb(${Math.round(r * f)},${Math.round(g * f)},${Math.round(b * f)})`;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? current + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawTextWithOutline(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, fillColor: string, outlineColor: string, outlineWidth: number) {
  ctx.strokeStyle = outlineColor;
  ctx.lineWidth = outlineWidth;
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fillColor;
  ctx.fillText(text, x, y);
}

async function renderCanvasFlyer(
  productImageSrc: string,
  postResult: PostResult,
  storeInfo: { name: string; phone: string; address: string },
  logoSrc?: string | null,
): Promise<string> {
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No canvas context");

  const ff = "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif";
  const headline = postResult.flyerHeadline || postResult.productName?.toUpperCase() || "HUGE SALE EVENT";
  const subheadline = postResult.flyerSubheadline || "";
  const promoLine = postResult.promoLine || "";
  const price = postResult.estimatedPrice || "";
  const cta = postResult.callToAction || "VISIT US TODAY!";
  const features = postResult.flyerDesign?.features || ["Free Delivery", "0% Financing", "Price Match Guarantee"];

  const c = {
    bg1: postResult.flyerDesign?.bgColor || "#1A1A2E",
    bg2: postResult.flyerDesign?.bgColorEnd || "#6B0F2A",
    accent: postResult.flyerDesign?.accentColor || "#FFD700",
    badge: postResult.flyerDesign?.badgeBg || "#E40000",
    headlineC: postResult.flyerDesign?.headlineColor || "#FFFFFF",
    text: postResult.flyerDesign?.textColor || "#F0F0F0",
  };

  const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
  bgGrad.addColorStop(0, c.bg1);
  bgGrad.addColorStop(0.35, c.bg2);
  bgGrad.addColorStop(0.65, lighten(c.bg2, 15));
  bgGrad.addColorStop(1, c.bg1);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  for (let i = 0; i < 200; i++) {
    const sx = Math.random() * W, sy = Math.random() * H;
    const size = Math.random() * 3 + 0.5;
    const alpha = Math.random() * 0.08;
    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.beginPath();
    ctx.arc(sx, sy, size, 0, Math.PI * 2);
    ctx.fill();
  }

  const headerH = 200;
  const headerGrad = ctx.createLinearGradient(0, 0, W, 0);
  headerGrad.addColorStop(0, darken(c.bg1, 30));
  headerGrad.addColorStop(0.5, darken(c.bg1, 10));
  headerGrad.addColorStop(1, darken(c.bg1, 30));
  ctx.fillStyle = headerGrad;
  ctx.fillRect(0, 0, W, headerH);

  ctx.fillStyle = c.accent;
  ctx.fillRect(0, headerH - 6, W, 6);

  let headerTextY = 60;
  if (logoSrc) {
    try {
      const logoImg = await loadImage(logoSrc);
      const logoH = 80;
      const logoW = (logoImg.width / logoImg.height) * logoH;
      const logoX = W / 2 - logoW / 2;
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 15;
      ctx.drawImage(logoImg, logoX, 20, logoW, logoH);
      ctx.shadowBlur = 0;
      headerTextY = 115;
    } catch {
      /* no logo */
    }
  }

  if (storeInfo.name) {
    const nameSize = fitText(ctx, storeInfo.name.toUpperCase(), W - 100, 52, 28, ff);
    ctx.font = `900 ${nameSize}px ${ff}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawTextWithOutline(ctx, storeInfo.name.toUpperCase(), W / 2, headerTextY, c.headlineC, darken(c.bg1, 50), 6);
  }

  const contactParts: string[] = [];
  if (storeInfo.address) contactParts.push(storeInfo.address);
  if (storeInfo.phone) contactParts.push(storeInfo.phone);
  if (contactParts.length) {
    ctx.font = `600 22px ${ff}`;
    ctx.fillStyle = c.accent;
    ctx.textAlign = "center";
    ctx.fillText(contactParts.join("  \u2022  "), W / 2, headerTextY + 38);
  }

  const headlineBannerY = headerH + 10;
  const headlineBannerH = subheadline ? 140 : 110;
  const bannerGrad = ctx.createLinearGradient(0, headlineBannerY, 0, headlineBannerY + headlineBannerH);
  bannerGrad.addColorStop(0, c.badge);
  bannerGrad.addColorStop(1, darken(c.badge, 25));
  ctx.fillStyle = bannerGrad;
  ctx.fillRect(0, headlineBannerY, W, headlineBannerH);

  ctx.save();
  ctx.globalAlpha = 0.1;
  for (let i = 0; i < 8; i++) {
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(i * 145, headlineBannerY, 70, headlineBannerH);
  }
  ctx.restore();

  ctx.font = `900 1px ${ff}`;
  const hlLines = headline.length > 20 ? wrapText(ctx, headline, 900) : [headline];
  if (hlLines.length === 1) {
    const hlS = fitText(ctx, headline, W - 80, 78, 40, ff);
    ctx.font = `900 ${hlS}px ${ff}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawTextWithOutline(ctx, headline, W / 2, headlineBannerY + (subheadline ? 52 : headlineBannerH / 2), "#FFFFFF", "rgba(0,0,0,0.6)", 8);
  } else {
    const lineH = 50;
    const startY = headlineBannerY + (subheadline ? 35 : headlineBannerH / 2 - (hlLines.length - 1) * lineH / 2);
    hlLines.forEach((line, i) => {
      const hlS = fitText(ctx, line, W - 80, 56, 32, ff);
      ctx.font = `900 ${hlS}px ${ff}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      drawTextWithOutline(ctx, line, W / 2, startY + i * lineH, "#FFFFFF", "rgba(0,0,0,0.6)", 6);
    });
  }

  if (subheadline) {
    const subS = fitText(ctx, subheadline, W - 120, 34, 20, ff);
    ctx.font = `700 ${subS}px ${ff}`;
    ctx.textAlign = "center";
    ctx.fillStyle = c.accent;
    ctx.fillText(subheadline, W / 2, headlineBannerY + headlineBannerH - 24);
  }

  const photoY = headlineBannerY + headlineBannerH + 20;
  const photoH = 550;
  const productImg = await loadImage(productImageSrc);

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.4)";
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 10;
  drawRoundedRect(ctx, 30, photoY, W - 60, photoH, 20);
  ctx.fillStyle = "#000";
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.restore();

  ctx.save();
  drawRoundedRect(ctx, 30, photoY, W - 60, photoH, 20);
  ctx.clip();
  const imgAspect = productImg.width / productImg.height;
  const areaAspect = (W - 60) / photoH;
  let sx = 0, sy = 0, sw = productImg.width, sh = productImg.height;
  if (imgAspect > areaAspect) {
    sw = productImg.height * areaAspect;
    sx = (productImg.width - sw) / 2;
  } else {
    sh = productImg.width / areaAspect;
    sy = (productImg.height - sh) / 2;
  }
  ctx.drawImage(productImg, sx, sy, sw, sh, 30, photoY, W - 60, photoH);
  ctx.restore();

  ctx.strokeStyle = `${c.accent}80`;
  ctx.lineWidth = 3;
  drawRoundedRect(ctx, 30, photoY, W - 60, photoH, 20);
  ctx.stroke();

  if (price) {
    const priceText = price;
    ctx.save();
    ctx.translate(W - 100, photoY + 60);
    ctx.rotate(0.12);
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 15;
    drawStarburst(ctx, 0, 0, 90, 58, 16);
    ctx.fillStyle = c.accent;
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.font = `800 16px ${ff}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = darken(c.accent, 60);
    ctx.fillText("ONLY", 0, -14);
    const priceS = fitText(ctx, priceText, 120, 36, 18, ff);
    ctx.font = `900 ${priceS}px ${ff}`;
    ctx.fillText(priceText, 0, 12);
    ctx.restore();
  }

  const promoY = photoY + photoH + 20;

  if (promoLine) {
    const promoH = 80;
    const promoGrad = ctx.createLinearGradient(0, promoY, W, promoY);
    promoGrad.addColorStop(0, c.accent);
    promoGrad.addColorStop(0.5, lighten(c.accent, 20));
    promoGrad.addColorStop(1, c.accent);
    ctx.fillStyle = promoGrad;
    ctx.fillRect(0, promoY, W, promoH);

    const pSize = fitText(ctx, promoLine.toUpperCase(), W - 100, 44, 22, ff);
    ctx.font = `900 ${pSize}px ${ff}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawTextWithOutline(ctx, promoLine.toUpperCase(), W / 2, promoY + promoH / 2, darken(c.accent, 70), "rgba(255,255,255,0.3)", 3);
  }

  const featY = promoLine ? promoY + 100 : promoY + 20;
  const featH = 130;
  ctx.fillStyle = `rgba(0,0,0,0.35)`;
  drawRoundedRect(ctx, 20, featY, W - 40, featH, 16);
  ctx.fill();

  const feat = features.slice(0, 3);
  while (feat.length < 3) feat.push("Shop Now");
  const fGap = 16;
  const fW = (W - 40 - fGap * 4) / 3;

  feat.forEach((f, i) => {
    const bx = 20 + fGap + i * (fW + fGap);
    const by = featY + 15;
    const bh = featH - 30;

    const cardGrad = ctx.createLinearGradient(bx, by, bx, by + bh);
    cardGrad.addColorStop(0, `${c.badge}90`);
    cardGrad.addColorStop(1, `${c.badge}40`);
    ctx.fillStyle = cardGrad;
    drawRoundedRect(ctx, bx, by, fW, bh, 12);
    ctx.fill();

    ctx.fillStyle = c.accent;
    ctx.fillRect(bx + 15, by, fW - 30, 4);

    ctx.font = `900 1px ${ff}`;
    const fLines = wrapText(ctx, f.toUpperCase(), fW - 40);
    const fSize = fitText(ctx, fLines[0] || f.toUpperCase(), fW - 30, 24, 14, ff);
    ctx.font = `800 ${fSize}px ${ff}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#FFFFFF";
    if (fLines.length > 1) {
      fLines.forEach((line, li) => {
        ctx.fillText(line, bx + fW / 2, by + bh / 2 + (li - (fLines.length - 1) / 2) * (fSize + 4));
      });
    } else {
      ctx.fillText(f.toUpperCase(), bx + fW / 2, by + bh / 2 + 4);
    }
  });

  const ctaY = featY + featH + 25;
  const ctaH = 80;
  const ctaGrad = ctx.createLinearGradient(100, ctaY, W - 100, ctaY + ctaH);
  ctaGrad.addColorStop(0, c.accent);
  ctaGrad.addColorStop(1, lighten(c.accent, 20));
  ctx.fillStyle = ctaGrad;
  drawRoundedRect(ctx, 80, ctaY, W - 160, ctaH, ctaH / 2);
  ctx.fill();

  ctx.shadowColor = "rgba(0,0,0,0.3)";
  ctx.shadowBlur = 15;
  ctx.shadowOffsetY = 5;
  ctx.fillStyle = ctaGrad;
  drawRoundedRect(ctx, 80, ctaY, W - 160, ctaH, ctaH / 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  const ctaSize = fitText(ctx, cta.toUpperCase(), W - 220, 38, 22, ff);
  ctx.font = `900 ${ctaSize}px ${ff}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = darken(c.accent, 70);
  ctx.fillText(cta.toUpperCase(), W / 2, ctaY + ctaH / 2 + 1);

  const footerH = 100;
  const footerY = H - footerH;
  ctx.fillStyle = darken(c.bg1, 40);
  ctx.fillRect(0, footerY, W, footerH);
  ctx.fillStyle = c.accent;
  ctx.fillRect(0, footerY, W, 4);

  if (storeInfo.name) {
    ctx.font = `800 28px ${ff}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = c.headlineC;
    ctx.fillText(storeInfo.name.toUpperCase(), W / 2, footerY + 35);
  }

  if (contactParts.length) {
    ctx.font = `500 20px ${ff}`;
    ctx.fillStyle = `${c.text}CC`;
    ctx.textAlign = "center";
    ctx.fillText(contactParts.join("  \u2022  "), W / 2, footerY + 68);
  }

  ctx.fillStyle = c.accent;
  ctx.fillRect(0, 0, W, 6);
  ctx.fillRect(0, H - 6, W, 6);
  ctx.fillRect(0, 0, 6, H);
  ctx.fillRect(W - 6, 0, 6, H);

  return canvas.toDataURL("image/png");
}

function parseHashtags(text: string): string[] {
  const matches = text.match(/#\w+/g);
  return matches ? matches.map((t) => t.replace("#", "")) : [];
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem("showroom_post_history");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]) {
  const cleaned = entries.slice(0, 30).map((e) => ({
    ...e,
    imagePreview: undefined,
    flyerDataUrl: undefined,
  }));
  try {
    localStorage.setItem("showroom_post_history", JSON.stringify(cleaned));
  } catch {
    localStorage.setItem("showroom_post_history", JSON.stringify(cleaned.slice(0, 10)));
  }
}

const STORE_INFO_KEY = "showroom_store_info";

function loadSavedStoreInfo(): { storeName: string; storePhone: string; storeAddress: string } | null {
  try {
    const raw = localStorage.getItem(STORE_INFO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoreInfo(info: { storeName: string; storePhone: string; storeAddress: string }) {
  localStorage.setItem(STORE_INFO_KEY, JSON.stringify(info));
}

function clearSavedStoreInfo() {
  localStorage.removeItem(STORE_INFO_KEY);
}

export default function CreatePost() {
  const { user } = useAuth();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegeneratingCaption, setIsRegeneratingCaption] = useState(false);
  const [isRegeneratingFlyer, setIsRegeneratingFlyer] = useState(false);
  const [result, setResult] = useState<PostResult | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [storeName, setStoreName] = useState("");
  const [storePhone, setStorePhone] = useState("");
  const [storeAddress, setStoreAddress] = useState("");
  const [storeInfoSaved, setStoreInfoSaved] = useState(false);
  const [flyerDataUrl, setFlyerDataUrl] = useState<string | null>(null);
  const [isGeneratingFlyer, setIsGeneratingFlyer] = useState(false);
  const [flyerMode, setFlyerMode] = useState<"ai" | "canvas" | null>(null);
  const [flyerError, setFlyerError] = useState(false);
  const [flyerDescription, setFlyerDescription] = useState("");
  const [platform, setPlatform] = useState<Platform>("facebook");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [isEditingFlyer, setIsEditingFlyer] = useState(false);
  const [editFlyerHeadline, setEditFlyerHeadline] = useState("");
  const [editFlyerSubheadline, setEditFlyerSubheadline] = useState("");
  const [editFlyerPrice, setEditFlyerPrice] = useState("");
  const [editFlyerPromo, setEditFlyerPromo] = useState("");
  const [editFlyerCTA, setEditFlyerCTA] = useState("");
  const [editFlyerFeatures, setEditFlyerFeatures] = useState<string[]>(["", "", "", ""]);
  const [editFlyerDescription, setEditFlyerDescription] = useState("");
  const [newHashtag, setNewHashtag] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const logoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setHistory(loadHistory());
    const savedLogo = localStorage.getItem("showroom_company_logo");
    if (savedLogo) {
      setLogoDataUrl(savedLogo);
    }
    const savedInfo = loadSavedStoreInfo();
    if (savedInfo) {
      setStoreName(savedInfo.storeName);
      setStorePhone(savedInfo.storePhone);
      setStoreAddress(savedInfo.storeAddress);
      setStoreInfoSaved(true);
    }
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const b64 = dataUrl.split(",")[1];
      setSelectedImage(dataUrl);
      setImageBase64(b64);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      processFile(file);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setLogoDataUrl(dataUrl);
      try { localStorage.setItem("showroom_company_logo", dataUrl); } catch {}
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoDataUrl(null);
    localStorage.removeItem("showroom_company_logo");
  };

  const toggleSaveStoreInfo = () => {
    if (storeInfoSaved) {
      clearSavedStoreInfo();
      setStoreInfoSaved(false);
    } else {
      saveStoreInfo({ storeName, storePhone, storeAddress });
      setStoreInfoSaved(true);
    }
  };

  const buildFlyer = useCallback(async (postResult: PostResult, imageSrc: string, onComplete?: () => void) => {
    setIsGeneratingFlyer(true);
    setFlyerDataUrl(null);
    setFlyerError(false);
    setFlyerMode(null);
    try {
      const dataUrl = await renderCanvasFlyer(
        imageSrc,
        postResult,
        { name: storeName, phone: storePhone, address: storeAddress },
        logoDataUrl,
      );
      setFlyerDataUrl(dataUrl);
      setFlyerMode("canvas");
    } catch (canvasErr: any) {
      console.error("Canvas flyer rendering failed:", canvasErr);
      try {
        const rawBase64 = imageSrc.startsWith("data:") ? imageSrc.split(",")[1] : imageSrc;
        const aiResp = await apiRequest<{ imageBase64?: string; error?: string }>("/post/generate-flyer-ai", {
          method: "POST",
          body: JSON.stringify({
            postResult,
            storeName,
            storePhone,
            storeAddress,
            productImageBase64: rawBase64,
            flyerDescription: flyerDescription || undefined,
          }),
        });
        if (aiResp.imageBase64) {
          setFlyerDataUrl(`data:image/png;base64,${aiResp.imageBase64}`);
          setFlyerMode("ai");
        } else {
          throw new Error(aiResp.error || "No image returned");
        }
      } catch (aiErr: any) {
        console.error("AI flyer fallback also failed:", aiErr);
        setFlyerError(true);
      }
    }
    setIsGeneratingFlyer(false);
    onComplete?.();
  }, [storeName, storePhone, storeAddress, logoDataUrl, flyerDescription]);

  const generate = async () => {
    if (!imageBase64) return;
    setIsGenerating(true);
    setError("");
    setFlyerDataUrl(null);
    setFlyerError(false);
    try {
      const data = await apiRequest<PostResult>("/post/generate", {
        method: "POST",
        body: JSON.stringify({
          imageBase64,
          storeName,
          storePhone,
          storeAddress,
          storeInfo: `${storeName || user?.storeLocation || "Furniture Pro"}`,
          platform,
          flyerDescription: flyerDescription || undefined,
        }),
      });
      setResult(data);
      setEditText(data.postText);
      const tags = data.tags || parseHashtags(data.postText);
      setHashtags(tags);
      setIsGenerating(false);
      buildFlyer(data, `data:image/jpeg;base64,${imageBase64}`);
      const entry: HistoryEntry = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
        platform,
        platform_label: PLATFORM_LABELS[platform],
        result: data,
        hashtags: tags,
      };
      const updated = [entry, ...loadHistory()].slice(0, 30);
      saveHistory(updated);
      setHistory(updated);
    } catch (err: any) {
      setError(err.message);
      setIsGenerating(false);
    }
  };

  const regenerateCaption = async () => {
    if (!imageBase64) return;
    setIsRegeneratingCaption(true);
    setError("");
    try {
      const data = await apiRequest<PostResult>("/post/generate", {
        method: "POST",
        body: JSON.stringify({
          imageBase64,
          storeName,
          storePhone,
          storeAddress,
          storeInfo: `${storeName || user?.storeLocation || "Furniture Pro"}`,
          platform,
          flyerDescription: flyerDescription || undefined,
        }),
      });
      setResult(data);
      setEditText(data.postText);
      const tags = data.tags || parseHashtags(data.postText);
      setHashtags(tags);
    } catch (err: any) {
      setError(err.message);
    }
    setIsRegeneratingCaption(false);
  };

  const regenerateFlyer = async () => {
    if (!result || !imageBase64) return;
    setIsRegeneratingFlyer(true);
    setFlyerDataUrl(null);
    setError("");
    try {
      await buildFlyer(result, `data:image/jpeg;base64,${imageBase64}`);
    } catch (err: any) {
      setError(err.message || "Failed to regenerate flyer");
    }
    setIsRegeneratingFlyer(false);
  };

  const openFlyerEditor = () => {
    if (!result) return;
    setEditFlyerHeadline(result.flyerHeadline || "");
    setEditFlyerSubheadline(result.flyerSubheadline || "");
    setEditFlyerPrice(result.estimatedPrice || "");
    setEditFlyerPromo(result.promoLine || "");
    setEditFlyerCTA(result.callToAction || "");
    const feat = result.flyerDesign?.features || [];
    setEditFlyerFeatures([feat[0] || "", feat[1] || "", feat[2] || "", feat[3] || ""]);
    setEditFlyerDescription(flyerDescription);
    setIsEditingFlyer(true);
  };

  const applyFlyerEdits = async () => {
    if (!result || !imageBase64) return;
    const updatedResult: PostResult = {
      ...result,
      flyerHeadline: editFlyerHeadline || result.flyerHeadline,
      flyerSubheadline: editFlyerSubheadline || result.flyerSubheadline,
      estimatedPrice: editFlyerPrice || result.estimatedPrice,
      promoLine: editFlyerPromo || result.promoLine,
      callToAction: editFlyerCTA || result.callToAction,
      flyerDesign: {
        ...(result.flyerDesign || { bgColor: "#0A1628", bgColorEnd: "#1B2D4A", accentColor: "#F59E0B", headlineColor: "#FFFFFF", textColor: "#E2E8F0", badgeBg: "#EF4444", badgeText: "#FFFFFF", decorStyle: "bold" as const }),
        features: editFlyerFeatures.filter(Boolean).length > 0 ? editFlyerFeatures : (result.flyerDesign?.features || []),
      },
    };
    setResult(updatedResult);
    if (editFlyerDescription !== flyerDescription) {
      setFlyerDescription(editFlyerDescription);
    }
    setIsEditingFlyer(false);
    setIsRegeneratingFlyer(true);
    setFlyerDataUrl(null);
    setError("");
    try {
      setIsGeneratingFlyer(true);
      setFlyerError(false);
      setFlyerMode(null);
      const dataUrl = await renderCanvasFlyer(
        `data:image/jpeg;base64,${imageBase64}`,
        updatedResult,
        { name: storeName, phone: storePhone, address: storeAddress },
        logoDataUrl,
      );
      setFlyerDataUrl(dataUrl);
      setFlyerMode("canvas");
    } catch (canvasErr: any) {
      console.error("Canvas flyer failed:", canvasErr);
      setFlyerError(true);
    }
    setIsGeneratingFlyer(false);
    setIsRegeneratingFlyer(false);
  };

  const copyText = async () => {
    const baseText = isEditing ? editText : result?.postText || "";
    const hashtagStr = hashtags.map((t) => `#${t}`).join(" ");
    const text = baseText.replace(/#\w+/g, "").trim() + "\n\n" + hashtagStr;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const saveEdit = () => {
    if (result) setResult({ ...result, postText: editText });
    setIsEditing(false);
  };

  const addHashtag = () => {
    const tag = newHashtag.replace(/[^a-zA-Z0-9_]/g, "").trim();
    if (tag && !hashtags.includes(tag)) {
      setHashtags([...hashtags, tag]);
    }
    setNewHashtag("");
  };

  const removeHashtag = (index: number) => {
    setHashtags(hashtags.filter((_, i) => i !== index));
  };

  const shareToFacebook = () => {
    const text = encodeURIComponent(result?.postText || "");
    window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, "_blank");
  };

  const shareToInstagram = () => {
    copyText();
    alert("Caption copied! Open Instagram and paste it when creating your post.");
  };

  const downloadFlyer = () => {
    if (!flyerDataUrl) return;
    const a = document.createElement("a");
    a.href = flyerDataUrl;
    a.download = `sale-graphic-${Date.now()}.png`;
    a.click();
  };

  const loadHistoryEntry = (entry: HistoryEntry) => {
    setResult(entry.result);
    setEditText(entry.result.postText);
    setHashtags(entry.hashtags || []);
    setPlatform(entry.platform);
    setShowHistory(false);
    setFlyerDataUrl(null);
    setImageBase64(null);
  };

  const deleteHistoryEntry = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = history.filter((h) => h.id !== id);
    saveHistory(updated);
    setHistory(updated);
  };

  const startOver = () => {
    setResult(null);
    setSelectedImage(null);
    setImageBase64(null);
    setError("");
    setIsEditing(false);
    setFlyerDataUrl(null);
    setHashtags([]);
  };

  return (
    <div className="page create-post-page">
      <div className="cp-banner">
        <div className="cp-banner-orb cp-banner-orb-1" />
        <div className="cp-banner-orb cp-banner-orb-2" />
        <div className="cp-banner-orb cp-banner-orb-3" />
        <div className="cp-banner-content">
          <div className="cp-banner-text">
            <h1>Create Post</h1>
            <p>Upload a product photo and AI creates a bold sale graphic + social media post</p>
          </div>
          <button className="cp-history-btn" onClick={() => setShowHistory(!showHistory)}>
            <Clock size={16} />
            History
            {history.length > 0 && <span className="cp-history-count">{history.length}</span>}
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="cp-history-panel card">
          <div className="card-header-row">
            <h3>Post History</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowHistory(false)}>
              <X size={14} />
            </button>
          </div>
          {history.length === 0 ? (
            <p className="cp-history-empty">No posts yet. Generate your first post!</p>
          ) : (
            <div className="cp-history-list">
              {history.map((entry) => (
                <div key={entry.id} className="cp-history-item" onClick={() => loadHistoryEntry(entry)}>
                  <div className="cp-history-item-info">
                    <strong>{entry.result.productName || "Post"}</strong>
                    <span className="cp-history-item-meta">
                      {PLATFORM_LABELS[entry.platform]} &middot; {new Date(entry.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="cp-history-item-actions">
                    <ChevronRight size={16} className="cp-history-arrow" />
                    <button className="cp-history-delete" onClick={(e) => deleteHistoryEntry(entry.id, e)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!result && (
        <div className="create-post-form">
          <div className="cp-form-grid">
            <div className="cp-form-left">
              <div className="card">
                <h3 className="cp-section-title">
                  <Upload size={18} />
                  Upload Product Photo
                </h3>
                <div
                  className={`upload-area ${isDragging ? "upload-area-dragging" : ""} ${selectedImage ? "upload-area-filled" : ""}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFileUpload} hidden />
                  {selectedImage ? (
                    <img src={selectedImage} alt="Selected" className="upload-preview" />
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon-wrap">
                        <Image size={32} />
                      </div>
                      <p className="upload-main-text">{isDragging ? "Drop your image here" : "Click or drag to upload"}</p>
                      <p className="upload-sub-text">Supports JPG, PNG, WebP</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="card store-info-card">
                <div className="cp-section-title-row">
                  <h3 className="cp-section-title">
                    <Store size={18} />
                    Store Information
                  </h3>
                  <button
                    className={`cp-save-toggle ${storeInfoSaved ? "cp-save-toggle-saved" : ""}`}
                    onClick={toggleSaveStoreInfo}
                    title={storeInfoSaved ? "Unsave store info" : "Save store info"}
                  >
                    {storeInfoSaved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                    {storeInfoSaved ? "Saved" : "Save"}
                  </button>
                </div>
                <p className="store-info-hint">This info will appear on the generated sales flyer</p>
                <div className="store-info-fields">
                  <div className="store-field">
                    <Store size={18} />
                    <input
                      type="text"
                      placeholder="Store Name (e.g., Johnson's Furniture)"
                      value={storeName}
                      onChange={(e) => { setStoreName(e.target.value); if (storeInfoSaved) { setStoreInfoSaved(false); clearSavedStoreInfo(); } }}
                    />
                  </div>
                  <div className="store-field">
                    <Phone size={18} />
                    <input
                      type="text"
                      placeholder="Phone (e.g., (555) 123-4567)"
                      value={storePhone}
                      onChange={(e) => { setStorePhone(e.target.value); if (storeInfoSaved) { setStoreInfoSaved(false); clearSavedStoreInfo(); } }}
                    />
                  </div>
                  <div className="store-field">
                    <MapPin size={18} />
                    <input
                      type="text"
                      placeholder="Address (e.g., 123 Main St)"
                      value={storeAddress}
                      onChange={(e) => { setStoreAddress(e.target.value); if (storeInfoSaved) { setStoreInfoSaved(false); clearSavedStoreInfo(); } }}
                    />
                  </div>
                </div>

                <div className="cp-logo-section">
                  <label className="cp-logo-label">
                    <ImagePlus size={16} />
                    Company Logo
                    <span className="cp-logo-hint">(appears on flyer header)</span>
                  </label>
                  <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoUpload} hidden />
                  {logoDataUrl ? (
                    <div className="cp-logo-preview-row">
                      <img src={logoDataUrl} alt="Logo" className="cp-logo-preview" />
                      <div className="cp-logo-actions">
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => logoRef.current?.click()}>Change</button>
                        <button type="button" className="btn btn-danger btn-sm" onClick={removeLogo}><X size={14} /></button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="cp-logo-upload-btn" onClick={() => logoRef.current?.click()}>
                      <ImagePlus size={18} />
                      Upload Logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="cp-form-right">
              <div className="card">
                <h3 className="cp-section-title">
                  <Sparkles size={18} />
                  Platform
                </h3>
                <div className="cp-chip-group">
                  {(["facebook", "instagram", "tiktok"] as Platform[]).map((p) => (
                    <button key={p} className={`cp-chip ${platform === p ? "cp-chip-active" : ""} cp-chip-${p}`} onClick={() => setPlatform(p)}>
                      {p === "facebook" && <Facebook size={14} />}
                      {p === "instagram" && <Instagram size={14} />}
                      {p === "tiktok" && <Share2 size={14} />}
                      {PLATFORM_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="card">
                <h3 className="cp-section-title">
                  <Sparkles size={18} />
                  Sale Details
                </h3>
                <p className="flyer-desc-hint">Tell AI what your sale is about — the more details, the better the graphic</p>
                <textarea
                  className="flyer-description-textarea"
                  placeholder={"e.g., Red Tag Clearance Event this weekend only!\n50% off all sectionals, $499 queen mattresses\nFree delivery + 0% financing for 60 months\nSale ends Saturday — Everything must go!"}
                  value={flyerDescription}
                  onChange={(e) => setFlyerDescription(e.target.value)}
                  rows={5}
                />
              </div>
            </div>
          </div>

          {error && <div className="login-error" style={{ marginTop: 12 }}>{error}</div>}
          {selectedImage && (
            <button className="btn btn-primary cp-generate-btn" onClick={generate} disabled={isGenerating}>
              {isGenerating ? <><Loader2 size={16} className="spinning" /> Generating Post & Flyer...</> : <><PenSquare size={16} /> Generate Post & Flyer</>}
            </button>
          )}
        </div>
      )}

      {isGenerating && (
        <div className="card cp-loading-card">
          <div className="loading-spinner" />
          <p style={{ textAlign: "center", color: "var(--text-muted)", marginTop: 12 }}>AI is crafting your {PLATFORM_LABELS[platform]} post &amp; sale graphic...</p>
          <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>Bold sale graphic will be generated next</p>
        </div>
      )}


      {result && (
        <>
          <div className="post-results">
            <div className="post-preview card">
              <div className="card-header-row">
                <h3>Generated Post</h3>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={regenerateCaption} disabled={isRegeneratingCaption}>
                    <RefreshCw size={14} className={isRegeneratingCaption ? "spinning" : ""} /> Regen
                  </button>
                  {isEditing ? (
                    <button className="btn btn-primary btn-sm" onClick={saveEdit}><Check size={14} /> Save</button>
                  ) : (
                    <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)}><Edit2 size={14} /> Edit</button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={copyText}>
                    {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
              </div>

              <div className="cp-platform-tone-row">
                <span className={`cp-result-badge cp-badge-${platform}`}>{PLATFORM_LABELS[platform]}</span>
                <span className="cp-result-badge cp-badge-tone">Sale Graphic</span>
              </div>

              {isEditing ? (
                <textarea className="post-edit-textarea" value={editText} onChange={(e) => setEditText(e.target.value)} rows={12} />
              ) : (
                <div className="post-text">{result.postText}</div>
              )}

              <div className="cp-hashtag-editor">
                <h4>Hashtags</h4>
                <div className="cp-hashtag-chips">
                  {hashtags.map((tag, i) => (
                    <span key={i} className="cp-hashtag-chip">
                      #{tag}
                      <button onClick={() => removeHashtag(i)} className="cp-hashtag-remove"><X size={12} /></button>
                    </span>
                  ))}
                  <div className="cp-hashtag-add">
                    <input
                      type="text"
                      placeholder="Add tag"
                      value={newHashtag}
                      onChange={(e) => setNewHashtag(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHashtag(); } }}
                    />
                    <button onClick={addHashtag} className="cp-hashtag-add-btn"><Plus size={14} /></button>
                  </div>
                </div>
              </div>

              {result.productName && (
                <div className="post-meta">
                  <span><strong>Product:</strong> {result.productName}</span>
                  {result.estimatedPrice && <span><strong>Price:</strong> {result.estimatedPrice}</span>}
                  {result.productCategory && <span><strong>Category:</strong> {result.productCategory}</span>}
                </div>
              )}

              <div className="share-buttons">
                <button className="btn share-btn facebook" onClick={shareToFacebook}><Facebook size={16} /> Facebook</button>
                <button className="btn share-btn instagram" onClick={shareToInstagram}><Instagram size={16} /> Instagram</button>
                <button className="btn share-btn tiktok" onClick={() => { copyText(); alert("Caption copied! Open TikTok to post."); }}>
                  <Share2 size={16} /> TikTok
                </button>
              </div>
            </div>

            <div className="post-images">
              {(flyerDataUrl || isGeneratingFlyer || isRegeneratingFlyer || flyerError) && (
                <div className="card flyer-card">
                  <div className="card-header-row">
                    <h3>Sale Graphic</h3>
                    <div style={{ display: "flex", gap: 6 }}>
                      {flyerDataUrl && !isEditingFlyer && (
                        <button className="btn btn-secondary btn-sm" onClick={openFlyerEditor} disabled={isRegeneratingFlyer || isGeneratingFlyer}>
                          <Edit2 size={14} /> Edit
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={regenerateFlyer} disabled={isRegeneratingFlyer || isGeneratingFlyer}>
                        <RefreshCw size={14} className={isRegeneratingFlyer ? "spinning" : ""} /> Regen
                      </button>
                      {flyerDataUrl && (
                        <button className="btn btn-primary btn-sm" onClick={downloadFlyer}>
                          <Download size={14} /> Download
                        </button>
                      )}
                    </div>
                  </div>

                  {(isGeneratingFlyer || isRegeneratingFlyer) && !flyerDataUrl ? (
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "60px 20px",
                      background: "linear-gradient(135deg, #0A1628, #1B2D4A)",
                      borderRadius: 12,
                      minHeight: 400,
                    }}>
                      <div className="loading-spinner" style={{ marginBottom: 16 }} />
                      <p style={{ color: "#94A3B8", textAlign: "center", fontSize: 15, marginBottom: 6 }}>
                        Generating your AI sale graphic...
                      </p>
                      <p style={{ color: "#64748B", textAlign: "center", fontSize: 12 }}>
                        This takes 30-60 seconds for a high-quality result
                      </p>
                    </div>
                  ) : flyerError && !flyerDataUrl ? (
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "60px 20px",
                      background: "linear-gradient(135deg, #0A1628, #1B2D4A)",
                      borderRadius: 12,
                      minHeight: 400,
                    }}>
                      <RefreshCw size={32} style={{ color: "#94A3B8", marginBottom: 12 }} />
                      <p style={{ color: "#F87171", textAlign: "center", fontSize: 15, marginBottom: 12 }}>
                        Sale graphic generation failed. Please try again.
                      </p>
                      <button className="btn btn-primary btn-sm" onClick={regenerateFlyer} disabled={isRegeneratingFlyer}>
                        <RefreshCw size={14} /> Try Again
                      </button>
                    </div>
                  ) : flyerDataUrl ? (
                    <>
                      <div style={{ position: "relative" }}>
                        {flyerMode && (
                          <span style={{
                            position: "absolute", top: 10, left: 10, zIndex: 2,
                            padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                            background: flyerMode === "ai" ? "rgba(16,185,129,0.9)" : "rgba(99,102,241,0.9)",
                            color: "#fff", backdropFilter: "blur(4px)",
                          }}>
                            {flyerMode === "ai" ? "AI Generated" : "Pro Flyer"}
                          </span>
                        )}
                        <img src={flyerDataUrl} alt="Sale Graphic" className="result-image flyer-image" style={{ borderRadius: 12 }} />
                      </div>
                      {isEditingFlyer && (
                        <div className="flyer-editor" style={{
                          marginTop: 16, padding: 16, background: "rgba(15,23,42,0.6)",
                          border: "1px solid rgba(139,92,246,0.3)", borderRadius: 12,
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                            <h4 style={{ margin: 0, fontSize: 15, color: "#E2E8F0" }}>Edit Flyer Content</h4>
                            <button className="btn btn-secondary btn-sm" onClick={() => setIsEditingFlyer(false)} style={{ padding: "4px 8px" }}>
                              <X size={14} />
                            </button>
                          </div>
                          <div style={{ display: "grid", gap: 10 }}>
                            <div>
                              <label style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4, display: "block" }}>Headline</label>
                              <input type="text" value={editFlyerHeadline} onChange={(e) => setEditFlyerHeadline(e.target.value)}
                                className="input" style={{ fontSize: 14 }} placeholder="e.g. HUGE RED TAG SALE!" />
                            </div>
                            <div>
                              <label style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4, display: "block" }}>Subheadline</label>
                              <input type="text" value={editFlyerSubheadline} onChange={(e) => setEditFlyerSubheadline(e.target.value)}
                                className="input" style={{ fontSize: 14 }} placeholder="e.g. 20-50% OFF This Weekend Only" />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                              <div>
                                <label style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4, display: "block" }}>Price</label>
                                <input type="text" value={editFlyerPrice} onChange={(e) => setEditFlyerPrice(e.target.value)}
                                  className="input" style={{ fontSize: 14 }} placeholder="e.g. $1,999" />
                              </div>
                              <div>
                                <label style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4, display: "block" }}>Call to Action</label>
                                <input type="text" value={editFlyerCTA} onChange={(e) => setEditFlyerCTA(e.target.value)}
                                  className="input" style={{ fontSize: 14 }} placeholder="e.g. HURRY IN TODAY!" />
                              </div>
                            </div>
                            <div>
                              <label style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4, display: "block" }}>Promo / Financing Line</label>
                              <input type="text" value={editFlyerPromo} onChange={(e) => setEditFlyerPromo(e.target.value)}
                                className="input" style={{ fontSize: 14 }} placeholder="e.g. 0% FINANCING FOR 60 MONTHS" />
                            </div>
                            <div>
                              <label style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4, display: "block" }}>Feature Badges (up to 4)</label>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                {editFlyerFeatures.map((f, i) => (
                                  <input key={i} type="text" value={f}
                                    onChange={(e) => { const arr = [...editFlyerFeatures]; arr[i] = e.target.value; setEditFlyerFeatures(arr); }}
                                    className="input" style={{ fontSize: 13 }} placeholder={`Feature ${i + 1}`} />
                                ))}
                              </div>
                            </div>
                            <div>
                              <label style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4, display: "block" }}>Creative Direction (optional)</label>
                              <textarea value={editFlyerDescription} onChange={(e) => setEditFlyerDescription(e.target.value)}
                                className="input" rows={2} style={{ fontSize: 13, resize: "vertical" }}
                                placeholder="e.g. Summer beach theme, bright colors, tropical vibes" />
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => setIsEditingFlyer(false)}>Cancel</button>
                            <button className="btn btn-primary btn-sm" onClick={applyFlyerEdits} disabled={isGeneratingFlyer || isRegeneratingFlyer}>
                              <Sparkles size={14} /> Apply & Regenerate
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
              {selectedImage && (
                <div className="card">
                  <h3 style={{ marginBottom: 12 }}>Original Photo</h3>
                  <img src={selectedImage} alt="Product" className="result-image" />
                </div>
              )}
            </div>
          </div>

          <button className="btn btn-secondary" onClick={startOver} style={{ marginTop: 16 }}>Start Over</button>
        </>
      )}
    </div>
  );
}
